import { useId, useState } from 'react'
import ComboBox from './ComboBox'
import { timesOfDay, usTimeZones, zoneLabel, type HintPlacement } from './dates'

export type TimePickerProps = {
  label: string
  hint?: string
  value?: string
  onChange?: (value: string) => void
  /** Earliest and latest times offered, in minutes after midnight. */
  min?: number
  max?: number
  /** Minutes between the times offered. */
  step?: number
  /** A 12-hour clock ("9:30 am", the default) or a 24-hour one ("09:30"). */
  clock?: 12 | 24
  /** Where the format hint shows; above the field by default. */
  hintPlacement?: HintPlacement
  /** Show a time zone select beside the time, starting from the visitor's own zone. */
  timeZone?: boolean
  onTimeZoneChange?: (zone: string) => void
  id?: string
}

const localZone = () => {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
  return usTimeZones.some((option) => option.zone === zone) ? zone : usTimeZones[0].zone
}

/**
 * A time field: a combo box of times at a set step, filtered as you type, with
 * a clock button that opens the full list, and an optional time zone.
 */
export default function TimePicker({
  label,
  clock = 12,
  hint = clock === 24 ? 'hh:mm' : 'hh:mm am',
  hintPlacement = 'above',
  value,
  onChange,
  min = 0,
  max = 24 * 60 - 1,
  step = 30,
  timeZone = false,
  onTimeZoneChange,
  id,
}: TimePickerProps) {
  const zoneId = useId()
  const [zone, setZone] = useState(localZone)
  return (
    <div className={`mds-time${timeZone ? ' mds-time--zone' : ''}`}>
      <ComboBox
        label={label}
        hint={hint}
        value={value}
        onChange={onChange}
        options={timesOfDay(min, max, step, clock)}
        icon="clock"
        hintPlacement={hintPlacement}
        id={id}
      />
      {timeZone && (
        <div className="mds-field mds-time__zone">
          <label className="mds-label" htmlFor={zoneId}>
            Time zone
          </label>
          <select
            id={zoneId}
            className="mds-select"
            value={zone}
            onChange={(event) => {
              setZone(event.target.value)
              onTimeZoneChange?.(event.target.value)
            }}
          >
            {usTimeZones.map((option) => (
              <option key={option.zone} value={option.zone}>
                {zoneLabel(option)}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  )
}
